import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ALL_CATEGORIES } from './CategoryResultsFilter';
import type { StandingsCategory } from '../../services/category-standings';

/** The query parameter a stage's results keep the picked class in. */
export const CATEGORY_PARAM = 'category';

/** `path` with the class picked carried along, for a link that keeps it. */
export function withCategory(path: string, category: string): string {
  return category === ALL_CATEGORIES ? path : `${path}?${new URLSearchParams({ [CATEGORY_PARAM]: category })}`;
}

/**
 * The class picked on a stage's results, kept in the URL (`?category=<id>`)
 * so the page can be reloaded, shared or come back to on that class — and so
 * the results page can hand its pick to the print page.
 *
 * Every class is the absence of the parameter. A class the stage does not run
 * (a stale link) reads as every class once the list is in; until then the pick
 * is trusted, so a link to one class does not flash all of them first.
 *
 * Changes replace the history entry rather than adding one: Back leaves the
 * page, it does not step through the classes looked at. Other parameters on
 * the page are left alone.
 */
export function useCategoryParam(categories: StandingsCategory[]): [string, (value: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get(CATEGORY_PARAM);

  const known = categories.length === 0 || categories.some(({ id }) => id === requested);
  const value = requested && known ? requested : ALL_CATEGORIES;

  const setValue = useCallback(
    (next: string) =>
      setSearchParams(
        (current) => {
          const params = new URLSearchParams(current);
          if (next === ALL_CATEGORIES) params.delete(CATEGORY_PARAM);
          else params.set(CATEGORY_PARAM, next);
          return params;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  return [value, setValue];
}
